import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MaterialModule } from '../../../shared/material.module';
import { Country } from '../../../models/country';
import { CompensationCategoryConfiguration, CreateCompensationCategoryConfigurationRequest, UpdateCompensationCategoryConfigurationRequest } from '../../../models/compensationCategoryConfiguration';
import { Observable, forkJoin } from 'rxjs';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { LutService } from '../../../services/lut.service';
import { Router } from '@angular/router';
import { Helpers } from '../../../utilities/helpers';
import { MatCardModule } from '@angular/material/card';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

interface CompensationCategory {
  bit: number;
  label: string;
}

@Component({
  selector: 'app-compensation-category-configuration',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatChipsModule,
    MatCardModule,
    NgxSpinnerModule
  ],
  templateUrl: './compensation-category-configuration.component.html',
  styleUrl: './compensation-category-configuration.component.scss'
})
export class CompensationCategoryConfigurationComponent implements OnInit {
  form!: FormGroup;
  helpers = Helpers;

  private lookupService = inject(LookupDataService);
  private lutService = inject(LutService);
  private toastService = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);

  countries$: Observable<Country[] | null>;
  countriesLoading$: Observable<boolean>;
  public countries: Country[] = [];

  public compensationCategories: CompensationCategory[] = [
    { bit: 0, label: 'Salary or Fees' },
    { bit: 1, label: 'Committee Meeting Fees' },
    { bit: 2, label: 'Bonus-Annual Incentive Plan' },
    { bit: 3, label: 'Taxable benefits' },
    { bit: 4, label: 'Stock Awards' },
    { bit: 5, label: 'Option Awards' },
    { bit: 6, label: 'Non-Equity Incentive Plan Compensation' },
    { bit: 7, label: 'Long Term' },
    { bit: 8, label: 'Pension value' },
    { bit: 9, label: 'Change in Pension Value and Nonqualified Deferred Compensation Earnings' },
    { bit: 10, label: 'Superannuation & pension benefits' },
    { bit: 11, label: 'Retirement (Post Employment) Benefit' },
    { bit: 12, label: 'Termination benefits' },
    { bit: 13, label: 'Long service leave' },
    { bit: 14, label: 'All Other' }
  ];

  public compensationCategoryConfigurations: CompensationCategoryConfiguration[] = [];
  private currentConfigurationId: string | null = null;
  private originalConfiguration: number = 0;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private formBuilder: FormBuilder,
    private router: Router) {
    this.form = this.formBuilder.group({
      country: ['', Validators.required],
      configuration: [0]
    });
    this.countries$ = this.lookupService.getVisibleCountries();
    this.countriesLoading$ = this.lookupService.getTableLoadingState('countries');
    this.countries$.subscribe(data => {
      if (data) {
        this.countries = data.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    // Subscribe to country changes to update configuration
    this.form.get('country')?.valueChanges.subscribe((countryId) => {
      this.updateConfigurationForCountry(countryId);
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.spinnerService.show();
    this.lutService.getCompensationCategoryConfigurations().subscribe({
      next: (configurations) => {
        this.compensationCategoryConfigurations = configurations;
        this.spinnerService.hide();

        // Update configuration if a country is already selected
        const selectedCountry = this.form.get('country')?.value;
        if (selectedCountry) {
          this.updateConfigurationForCountry(selectedCountry);
        }
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.spinnerService.hide();
        this.toastService.error('Error loading data');
      }
    });
  }

  updateConfigurationForCountry(countryId: string): void {
    if (!countryId) {
      this.form.get('configuration')?.setValue(0);
      this.currentConfigurationId = null;
      this.originalConfiguration = 0;
      return;
    }

    // Find configuration for the selected country
    const configForCountry = this.compensationCategoryConfigurations.find(
      config => config.countryId === countryId
    );

    if (configForCountry) {
      this.form.get('configuration')?.setValue(configForCountry.configuration);
      this.currentConfigurationId = configForCountry.id;
      this.originalConfiguration = configForCountry.configuration;
    } else {
      this.form.get('configuration')?.setValue(0);
      this.currentConfigurationId = null;
      this.originalConfiguration = 0;
    }
  }

  isCategorySelected(bit: number): boolean {
    const configuration = this.form.get('configuration')?.value || 0;
    return (configuration & (1 << bit)) !== 0;
  }

  toggleCategory(bit: number): void {
    const configuration = this.form.get('configuration')?.value || 0;
    const newConfiguration = configuration ^ (1 << bit);
    this.form.get('configuration')?.setValue(newConfiguration);
  }

  hasChanges(): boolean {
    const currentConfiguration = this.form.get('configuration')?.value || 0;
    return currentConfiguration !== this.originalConfiguration;
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    const countryId = this.form.get('country')?.value;
    const configuration = this.form.get('configuration')?.value || 0;

    this.spinnerService.show();

    if (this.currentConfigurationId) {
      // Update existing configuration
      const request: UpdateCompensationCategoryConfigurationRequest = {
        countryId: countryId,
        configuration: configuration
      };
      this.lutService.updateCompensationCategoryConfiguration(this.currentConfigurationId, request).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.toastService.success('Configuration updated successfully');
          this.loadData();
        },
        error: (error) => {
          console.error('Error updating configuration:', error);
          this.spinnerService.hide();
          this.toastService.error('Error updating configuration');
        }
      });
    } else {
      // Create new configuration
      const request: CreateCompensationCategoryConfigurationRequest = {
        countryId: countryId,
        configuration: configuration
      };
      this.lutService.createCompensationCategoryConfiguration(request).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.toastService.success('Configuration created successfully');
          this.loadData();
        },
        error: (error) => {
          console.error('Error creating configuration:', error);
          this.spinnerService.hide();
          this.toastService.error('Error creating configuration');
        }
      });
    }
  }
}
